import { Skeleton } from "@/components/loaders/Skeleton";
import { TooltipV2 } from "@/components/TooltipV2";
import { classNames } from "@/utils/classnames";
import { forwardRef, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/loaders/spinner";
import { prettyFormatDurationSeconds } from "@/utils/date-time";

export enum ChartStyle {
  CLASSIC = "CLASSIC",
  // WIP
  NO_BORDER = "NO_BORDER",
}

export interface ChartWrapperProps {
  children?: ReactNode;
  title: ReactNode;
  testId?: string;
  titleClassName?: string;
  fillHeight?: boolean;
  width?: string | number;
  height?: string | number;
  subheaderOverride?: ReactNode;
  hideWindowPeriodDisplay?: boolean;
  windowPeriodSeconds?: number | null;
  loading?: boolean;
  toolbar?: ReactNode;
  yAxisHoverLabel?: ReactNode;
  unitsLabel?: string;
  style?: ChartStyle;
  solidBackground?: boolean;
  /** Allow overlays rendered inside the chart plot to extend beyond the card. */
  overflowVisible?: boolean;
}

export const CHART_TITLE_TYPOGRAPHY_CLASS_NAME = "font-medium text-zinc-700 dark:!text-zinc-50";
export const CHART_PANEL_PADDING_CLASS_NAME = "p-cds-12 pl-cds-16 pr-cds-8";

/** Caption + 12px padding top and bottom + two borders, for a host that sizes the plot itself.
 * Under-reserving grows the card past its cell, whose clip then eats the bottom border. */
export const CHART_CARD_CHROME_PX = 66;

const ChartTitle: React.FC<{ title: ReactNode }> = ({ title }) => {
  const titleContainerRef = useRef<HTMLSpanElement | null>(null);
  const titleMeasureRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const containerElement = titleContainerRef.current;
    const measureElement = titleMeasureRef.current;
    if (containerElement == null || measureElement == null) {
      return;
    }

    const naturalTitleWidth = measureElement.getBoundingClientRect().width;
    const availableTitleWidth = containerElement.getBoundingClientRect().width;
    const nextIsTruncated = Math.ceil(naturalTitleWidth) > Math.ceil(availableTitleWidth);
    setIsTruncated((previousIsTruncated) =>
      previousIsTruncated === nextIsTruncated ? previousIsTruncated : nextIsTruncated,
    );
  }, []);

  useEffect(() => {
    updateTruncation();

    const containerElement = titleContainerRef.current;
    const measureElement = titleMeasureRef.current;
    if (containerElement == null || measureElement == null) {
      return;
    }

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateTruncation);
      return () => window.removeEventListener("resize", updateTruncation);
    }

    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(containerElement);
    resizeObserver.observe(measureElement);

    return () => resizeObserver.disconnect();
  }, [title, updateTruncation]);

  return (
    <TooltipV2 content={title} disabled={!isTruncated} placement={"bottom-start"}>
      <span className={"relative block min-w-0 max-w-full flex-1 cursor-default overflow-hidden"}>
        <span ref={titleContainerRef} className={"block min-w-0 max-w-full overflow-hidden"}>
          <span
            className={"block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap"}
          >
            {title}
          </span>
        </span>
        <span
          ref={titleMeasureRef}
          aria-hidden={true}
          className={
            "invisible pointer-events-none absolute left-0 top-0 block w-max max-w-none whitespace-nowrap"
          }
        >
          {title}
        </span>
      </span>
    </TooltipV2>
  );
};

// Thing to make sure we're always rendering something that's the right size
export const ChartWrapper = forwardRef<HTMLElement, ChartWrapperProps>(function ChartWrapper(
  {
    children,
    title,
    testId,
    titleClassName,
    fillHeight = true,
    width,
    loading,
    height,
    toolbar,
    yAxisHoverLabel,
    style = ChartStyle.CLASSIC,
    subheaderOverride,
    windowPeriodSeconds,
    hideWindowPeriodDisplay = false,
    overflowVisible = false,
  },
  forwardedRef,
) {
  const hasTitle = title != null && title !== "";
  const formattedWindowPeriod =
    windowPeriodSeconds != null ? prettyFormatDurationSeconds(windowPeriodSeconds) : null;
  const isClassic = style === ChartStyle.CLASSIC;

  return (
    <figure
      data-page-ui={isClassic ? "full" : undefined}
      data-testid={testId}
      className={classNames(
        "flex min-w-0 max-w-full flex-col items-center justify-between",
        overflowVisible ? "overflow-visible" : "overflow-hidden",
        fillHeight ? "h-full flex-grow" : "shrink-0",
        "group/chart isolate",
        "w-full",
        isClassic &&
          classNames(
            "rounded-cds-6 border border-cds-surface-border dark:!border-cds-ink",
            CHART_PANEL_PADDING_CLASS_NAME,
            "bg-cds-surface-hover dark:bg-cds-raised-dark",
          ),
      )}
      ref={forwardedRef}
    >
      <div
        className={"flex w-full justify-between items-start gap-2 flex-1 border-cds-light-gray-300"}
      >
        <figcaption
          className={
            "flex w-full min-w-0 grow flex-col gap-cds-4 overflow-hidden text-ellipsis text-zinc-700 dark:!text-zinc-50"
          }
        >
          <div
            className={classNames(
              "flex min-w-0 max-w-full items-center gap-1 text-ellipsis",
              CHART_TITLE_TYPOGRAPHY_CLASS_NAME,
              titleClassName ?? "text-cds-lg",
            )}
          >
            <ChartTitle title={title} />

            {/* On title-less charts this row is empty (zero height) when loaded,
                so the spinner moves to the subtitle line to avoid a phantom row. */}
            {loading && hasTitle && (
              <Spinner className={classNames("opacity-50 drop-shadow-sm z-10")} size="sm" />
            )}
          </div>
          {/* {unitsLabel ? (
            <div className={"text-zinc-400 text-cds-sm text-ellipsis flex-shrink-0"}>
              {" "}
              ({unitsLabel})
            </div>
          ) : null} */}

          {subheaderOverride != null ? (
            subheaderOverride
          ) : !hideWindowPeriodDisplay && windowPeriodSeconds != null ? (
            <span className={"cursor-default text-cds-xs text-cds-ink-subtle dark:text-zinc-400"}>
              {formattedWindowPeriod} windows
            </span>
          ) : loading ? (
            // Reserve the subtitle line at the real text-cds-xs line box so the
            // header keeps the same height when data arrives.
            <span aria-hidden className={"flex h-4 items-center gap-2"}>
              <Skeleton className={"h-3 w-24"} shape="text" tone="subtle" />
              {!hasTitle && (
                <Spinner className={classNames("opacity-50 drop-shadow-sm z-10")} size="sm" />
              )}
            </span>
          ) : null}
        </figcaption>
        {/* Render the toolbar even while loading: its actions (edit/duplicate/delete/fullscreen)
            operate on config, not data, so the menu stays reachable if the snapshot never resolves. */}
        {toolbar != null && (
          <div className={"-mt-cds-4 flex min-h-7 flex-shrink-0 items-center"}>{toolbar}</div>
        )}
      </div>
      <div
        className={classNames(
          "relative min-w-0 max-w-full",
          overflowVisible ? "overflow-visible" : "overflow-hidden",
        )}
        style={{
          width,
          height: fillHeight ? undefined : height,
          minWidth: width,
          minHeight: fillHeight ? height : undefined,
        }}
      >
        {yAxisHoverLabel != null && (
          <div
            className={"group/y-axis-label absolute left-0 top-0 z-10 h-full w-12 cursor-default"}
          >
            <div
              className={
                "pointer-events-none absolute left-cds-4 top-1/2 -translate-y-1/2 -rotate-90 rounded-cds-3 bg-cds-surface-hover px-cds-4 py-cds-2 text-cds-2xs text-cds-ink-subtle opacity-0 shadow-cds-sm transition-opacity duration-100 group-hover/y-axis-label:opacity-100 dark:bg-cds-raised-dark dark:text-zinc-400"
              }
            >
              {yAxisHoverLabel}
            </div>
          </div>
        )}
        {children}
      </div>
    </figure>
  );
});
